import React, { FC, memo, useEffect, useMemo } from 'react';
import { RouteComponentProps, useNavigate } from '@reach/router';
import { useTranslation } from 'react-i18next';

import { PageLayout } from 'components/PageLayout';
import { Card } from 'components/Card';
import { Icon } from 'components/Icon';
import { Header } from 'components/PageLayout/Header';
import { useCheckoutSessionContext } from 'hooks/context/checkoutSession';
import { extractBasePath } from 'hooks/context/checkoutSession/utils';
import { isCheckoutSessionPaid } from 'services/checkout-service/helpers';
import { InvoiceSummary } from 'components/InvoiceSummary';
import { CheckoutPayResponse, CheckoutType } from 'services/checkout-service/models';
import { extractMerchantBusinessLogo } from 'utils/Logo';

import { ConfirmationTotals } from './ConfirmationTotals';
import { PaymentSourceInfo } from './PaymentSourceInfo';
import { CONFIRMATION_TEST_IDS } from './const';

import styles from './styles.module.scss';

const hasPayResponseLocationState = (state?: any): state is CheckoutPayResponse => {
  return !!(state?.id);
};

const Confirmation: FC<RouteComponentProps<{checkoutSessionId: Uuid}>> = memo(({ checkoutSessionId, location }) => {
  const { t } = useTranslation('common');

  const { checkoutSession, merchantSettings, error } = useCheckoutSessionContext();

  const navigate = useNavigate();

  const payResponse = location?.state as CheckoutPayResponse;

  const { type } = checkoutSession || {};

  const isPayWidget = useMemo(() => type === CheckoutType.PAY_WIDGETS, [type]);

  useEffect(() => {
    return () => {
      // added this here since reach router persists the location
      // state even after refreshing the page
      if (hasPayResponseLocationState(payResponse)) {
        navigate('.', { state: '', replace: true });
      }
    };
  });

  useEffect(() => {
    // if checkout session is not paid meaning it can be unpaid, expired or max pay attempts reached,
    // then redirect to the root page
    if (!isCheckoutSessionPaid(checkoutSession?.status) && !hasPayResponseLocationState(payResponse)) {
      const basePath = extractBasePath(window.location.pathname);
      navigate(`/${basePath}/${checkoutSessionId}`, { replace: true });
    }
  }, [checkoutSessionId, payResponse, checkoutSession, navigate]);

  if (error) {
    console.error('Error fetching checkout session');
    throw new Error(t('errorLoadingPage'));
  }

  if (!checkoutSession) return null;

  const { invoice, customer, merchant: { name, logos }, paymentDetails, shoppingCart } = checkoutSession;

  const { id, notes } = invoice || {};
  const isInvoiceMode = !!invoice;

  let { cardType, lastFour, amount = 0, additionalCharges = [], id: transactionId, orderNote, paymentMethodDetails } = paymentDetails || {};
  const { tipAmount, totalTaxAmount, subtotal = 0 } = shoppingCart || {};

  if (hasPayResponseLocationState(payResponse)) {
    // eslint-disable-next-line prefer-destructuring
    cardType = payResponse.cardType;
    // eslint-disable-next-line prefer-destructuring
    lastFour = payResponse.lastFour;
    // eslint-disable-next-line prefer-destructuring
    paymentMethodDetails = payResponse.paymentMethodDetails;
    // eslint-disable-next-line prefer-destructuring
    amount = payResponse.amount;
    // eslint-disable-next-line prefer-destructuring
    additionalCharges = payResponse.additionalCharges ?? [];
    transactionId = payResponse.id ?? '';
    orderNote = payResponse.orderNote ?? '';
  }

  const { BUSINESS_NAME, HEADER_COLOR, BACKGROUND_COLOR, FOOTER_COLOR, FOOTER_TEXT, LOGO_SIZE } = merchantSettings || {};

  return (
    <PageLayout
      customBodyColor={BACKGROUND_COLOR}
      customFooterColor={FOOTER_COLOR}
      customFooterText={FOOTER_TEXT}
      data-testid={CONFIRMATION_TEST_IDS.root}
      header={
        <Header
          customHeaderColor={HEADER_COLOR}
          isInvoiceMode={isInvoiceMode}
          merchantName={BUSINESS_NAME && !isInvoiceMode ? BUSINESS_NAME : name}
          merchantLogo={extractMerchantBusinessLogo(logos)}
          logoSize={LOGO_SIZE}
        />
      }
      isInvoiceMode={isInvoiceMode}
    >
      <div data-testid={CONFIRMATION_TEST_IDS.status} className={styles.status}>
        <Icon className={styles.icon} name="checkMark" />
        <h1>{t('confirmation.title')}</h1>
      </div>

      <Card className={styles.card}>
        {(customer?.firstName || isPayWidget) && (
          <div className={styles.salutation}>
            <h2 data-testid={CONFIRMATION_TEST_IDS.firstName} className="h6">
              {t('confirmation.heyName', {
                firstName: customer?.firstName || '',
              })}
            </h2>
            <p>{t('confirmation.paymentReceived')}</p>
          </div>
        )}

        {isInvoiceMode && <InvoiceSummary id={id} notes={notes} />}

        <PaymentSourceInfo cardType={cardType} lastFour={lastFour} paymentMethodDetails={paymentMethodDetails} />

        {transactionId && isPayWidget && (
          <div className={styles.transactionId}>
            <h2 className="h6">{t('confirmation.transactionId')}</h2>
            <p data-testid={CONFIRMATION_TEST_IDS.transactionId}>
              {transactionId}
            </p>
          </div>
        )}

        <ConfirmationTotals
          amount={subtotal || amount}
          additionalCharges={additionalCharges}
          tipAmount={tipAmount}
          taxAmount={totalTaxAmount}
        />

        {orderNote && (
          <div className={styles.orderNote}>
            <h2 className="h6">{t('note.title')}</h2>
            <p data-testid={CONFIRMATION_TEST_IDS.orderNote}>{orderNote}</p>
          </div>
        )}
      </Card>
    </PageLayout>
  );
});

export { Confirmation };
